// src/api/nom35AnalyticsApi.js
import apiClient from "./apiClient";
import nomAnalyticsFilters from "./nomAnalyticsFilters";

function filenameFromContentDisposition(disposition, fallback) {
  if (!disposition) return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^"]+)"?/i.exec(disposition);
  if (!match) return fallback;
  try { return decodeURIComponent(match[1]); } catch { return match[1]; }
}

const nom35AnalyticsApi = {
  getScopeConfig: async (scopeId, filters = {}) => {
    const response = await apiClient.get(`/analytics/nom035/${scopeId}/scope-config`, { params: nomAnalyticsFilters(scopeId, filters) });
    return response.data;
  },
  /**
   * Obtener resumen de niveles de riesgo (global/category/domain) para una empresa
   */
  getSummaryByCompanyId: async (companyId, metric, filters = {}) => {
    try {
      const response = await apiClient.get(
        `/analytics/nom035/${companyId}/responses/summary`,
        { params: { metric, ...nomAnalyticsFilters(companyId, filters) } }
      );
      return response.data;
    } catch (error) {
      console.error("Error obteniendo resumen NOM-035 por empresa:", error);
      throw error;
    }
  },

  /**
   * Obtener resumen por áreas de trabajo para una empresa
   */
  getJobAreasSummaryByCompanyId: async (companyId, metric, filters = {}) => {
    try {
      const response = await apiClient.get(
        `/analytics/nom035/${companyId}/summary/jobareas`,
        { params: { metric, ...nomAnalyticsFilters(companyId, filters) } }
      );
      return response.data;
    } catch (error) {
      console.error("Error obteniendo resumen por áreas de trabajo NOM-035 por empresa:", error);
      throw error;
    }
  },

  /**
   * Resumen global de niveles de riesgo (todas las empresas)
   */
  getSummaryGlobal: async (metric, filters = {}) => {
    try {
      const response = await apiClient.get(
        `/analytics/nom035/responses/summary`,
        { params: { metric, ...filters } }
      );
      return response.data;
    } catch (error) {
      console.error("Error obteniendo resumen global NOM-035:", error);
      throw error;
    }
  },

  /**
   * Resumen requireClinicalAttention por empresa
   */
  getClinicalSummaryByCompanyId: async (companyId, filters = {}) => {
    try {
      const response = await apiClient.get(
        `/analytics/nom035/${companyId}/responses/clinical-summary`,
        { params: nomAnalyticsFilters(companyId, filters) }
      );
      return response.data;
    } catch (error) {
      console.error("Error obteniendo clinical summary NOM-035 por empresa:", error);
      throw error;
    }
  },

  /**
   * Resumen global requireClinicalAttention
   */
  getClinicalSummaryGlobal: async (filters = {}) => {
    try {
      const response = await apiClient.get(
        `/analytics/nom035/responses/clinical-summary`,
        { params: filters }
      );
      return response.data;
    } catch (error) {
      console.error("Error obteniendo clinical summary global NOM-035:", error);
      throw error;
    }
  },

  /**
   * Detalle de preguntas + promedios por empresa y métrica
   */
  getQuestionsByCompanyId: async (companyId, metric, filters = {}) => {
    try {
      const response = await apiClient.get(
        `/analytics/nom035/${companyId}/questions/${metric}`,
        { params: nomAnalyticsFilters(companyId, filters) }
      );
      return response.data;
    } catch (error) {
      console.error("Error obteniendo preguntas NOM-035 por empresa:", error);
      throw error;
    }
  },

  /**
   * Detalle de preguntas + promedios global por métrica
   */
  getQuestionsGlobal: async (metric, filters = {}) => {
    try {
      const response = await apiClient.get(
        `/analytics/nom035/questions/${metric}`,
        { params: filters }
      );
      return response.data;
    } catch (error) {
      console.error("Error obteniendo preguntas globales NOM-035:", error);
      throw error;
    }
  },

  /**
   * Total de respuestas por empresa
   */
  getResponsesCountByCompanyId: async (companyId, filters = {}) => {
    try {
      const response = await apiClient.get(
        `/analytics/nom035/${companyId}/responses/count`,
        { params: nomAnalyticsFilters(companyId, filters) }
      );
      return response.data;
    } catch (error) {
      console.error("Error obteniendo total de respuestas por empresa:", error);
      throw error;
    }
  },

  /**
   * Total global de respuestas
   */
  getResponsesCountGlobal: async () => {
    try {
      const response = await apiClient.get(`/analytics/nom035/responses/count`);
      return response.data;
    } catch (error) {
      console.error("Error obteniendo total global de respuestas:", error);
      throw error;
    }
  },

  /**
   * Lista de respondants (JSON)
   */
  getRespondantsByCompanyId: async (companyId, filters = {}) => {
    try {
      const response = await apiClient.get(
        `/analytics/nom035/${companyId}/respondants`,
        { params: nomAnalyticsFilters(companyId, filters) }
      );
      return response.data;
    } catch (error) {
      console.error("Error obteniendo respondants NOM-035 por empresa:", error);
      throw error;
    }
  },

  /**
   * Export respondants (Excel): devuelve { blob, filename }
   */
  getRespondantsByCompanyIdExport: async (companyId, filters = {}) => {
    try {
      const response = await apiClient.get(
        `/analytics/nom035/${companyId}/respondants/export`,
        {
          responseType: "blob",
          params: nomAnalyticsFilters(companyId, filters),
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        }
      );

      const filename = filenameFromContentDisposition(
        response.headers["content-disposition"],
        `nom035-respondants-${companyId}.xlsx`
      );

      return { blob: response.data, filename };
    } catch (error) {
      console.error("Error exportando respondants NOM-035 por empresa:", error);
      throw error;
    }
  },

  /**
   * Helper para descargar directo en el navegador
   */
  downloadRespondantsByCompanyIdExcel: async (companyId, filters = {}) => {
    const { blob, filename } =
      await nom35AnalyticsApi.getRespondantsByCompanyIdExport(companyId, filters);

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `nom035-respondants-${companyId}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

export default nom35AnalyticsApi;
